import React, { useState } from 'react';
import { ChevronDown, ChevronUp, ShieldCheck, MapPin } from 'lucide-react';
import { RIGHTS, SCOTLAND_RIGHTS } from '../data/content';
import PrintButton from '../components/PrintButton';
import SEO from '../components/SEO';
import HelplinesStrip from '../components/HelplinesStrip';
import NationPicker from '../components/NationPicker';
import { IfScotland, useNation } from '../i18n/NationContext';

export default function Rights() {
  const { nation } = useNation();
  const rights = nation === 'scotland' ? SCOTLAND_RIGHTS : RIGHTS;
  const [openIds, setOpenIds] = useState(new Set());

  const toggle = (id) =>
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="max-w-3xl mx-auto px-5 sm:px-8 py-10" data-testid="rights-screen">
      <SEO
        title="Your Rights — Sexual Violence Survivors UK"
        description="Lifetime anonymity, ISVA support, special measures in court, Victim Personal Statements and more — 10 UK legal rights for survivors of sexual offences."
        path="/app/rights"
      />
      <p className="eyebrow">YOUR RIGHTS AS A SURVIVOR</p>
      <h1
        className="font-heading mt-3"
        style={{ fontSize: 'clamp(28px, 4vw, 36px)', lineHeight: 1.15, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
      >
        What the law guarantees you.
      </h1>
      <p
        className="font-body mt-4 max-w-xl"
        style={{ fontSize: 14.5, color: 'var(--tf-text-secondary)', lineHeight: 1.65 }}
      >
        You have specific rights as a survivor of a sexual offence. Choose your nation
        below so we can show you the right protections, agencies and advocacy. Tap any right
        to read more.
      </p>

      <NationPicker testid="rights-nation-picker" />

      <HelplinesStrip variant="sv" />

      <div className="mt-6 flex gap-2 flex-wrap">
        <PrintButton label="Print / Save this page" />
      </div>

      <IfScotland>
      <section
        className="mt-8 tf-card p-5 sm:p-6"
        data-testid="rights-scotland-scope"
        style={{
          background: 'linear-gradient(180deg, #FFFFFF 0%, #F4EFF8 100%)',
          borderColor: 'var(--tf-purple-light)',
        }}
      >
        <div className="flex items-start gap-4">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center flex-shrink-0"
            style={{
              background: 'linear-gradient(135deg, var(--tf-purple) 0%, var(--tf-pink) 100%)',
              color: '#fff',
            }}
            aria-hidden="true"
          >
            <MapPin size={20} />
          </div>
          <div className="min-w-0 flex-1">
            <p
              className="font-body font-bold"
              style={{ fontSize: 10.5, letterSpacing: '0.22em', color: 'var(--tf-purple-dark)' }}
            >
              A COMPLETELY SEPARATE LEGAL SYSTEM
            </p>
            <h2
              className="font-heading mt-1"
              style={{ fontSize: 'clamp(20px, 2.4vw, 23px)', color: 'var(--tf-text)', letterSpacing: '-0.01em', lineHeight: 1.25 }}
            >
              You are reading your rights in Scotland.
            </h2>
            <p
              className="font-body mt-3"
              style={{ fontSize: 13.5, color: 'var(--tf-text-secondary)', lineHeight: 1.7 }}
            >
              Your rights in Scotland come from the <strong>Victims and Witnesses
              (Scotland) Act 2014</strong>, the <strong>Vulnerable Witnesses (Scotland)
              Acts</strong>, and the reforms in the <strong>Victims, Witnesses, and
              Justice Reform (Scotland) Act 2025</strong> — delivered by Police
              Scotland, COPFS and the Scottish Courts &amp; Tribunals Service. Every right
              below applies in Scotland.
            </p>
          </div>
        </div>
      </section>
      </IfScotland>

      <div className="mt-8 flex flex-col gap-3" data-testid={nation === 'scotland' ? 'rights-list-scotland' : 'rights-list-ew'}>
        {rights.map((r) => {
          const open = openIds.has(r.id);
          return (
            <button
              key={r.id}
              type="button"
              onClick={() => toggle(r.id)}
              data-testid={`right-${r.id}`}
              className="tf-card text-left p-5 transition-shadow"
              style={{ cursor: 'pointer' }}
            >
              <div className="flex items-center gap-4">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: 'var(--tf-purple-light)', color: 'var(--tf-purple-dark)' }}
                >
                  <ShieldCheck size={18} />
                </div>
                <h3
                  className="font-heading flex-1"
                  style={{ fontSize: 17, color: 'var(--tf-text)', letterSpacing: '-0.01em' }}
                >
                  {r.title}
                </h3>
                {open ? (
                  <ChevronUp size={18} color="var(--tf-text-muted)" />
                ) : (
                  <ChevronDown size={18} color="var(--tf-text-muted)" />
                )}
              </div>

              <div
                className="mt-4 pt-4 tf-prose print-show"
                data-print-expand="true"
                style={{
                  borderTop: '1px solid var(--tf-card-border)',
                  display: open ? 'block' : 'none',
                }}
              >
                <p style={{ fontSize: 14, lineHeight: 1.7 }}>{r.body}</p>
              </div>
            </button>
          );
        })}
      </div>

      <div
        className="mt-10 p-5 rounded-2xl"
        style={{ background: 'var(--tf-pink-soft)' }}
      >
        <p
          className="font-body font-bold"
          style={{ fontSize: 13, color: 'var(--tf-text)', marginBottom: 4 }}
        >
          You can use these rights without reporting to the police.
        </p>
        <p
          className="font-body"
          style={{ fontSize: 12.5, color: 'var(--tf-text-secondary)', lineHeight: 1.6 }}
        >
          {nation === 'scotland'
            ? "Rape Crisis Scotland advocacy, Scottish Women's Aid, ASSIST and Archway SARC are always free and confidential — whether or not you speak to Police Scotland."
            : 'Specialist support and the right to be heard exist independently of any criminal case.'}
        </p>
      </div>
    </div>
  );
}
